import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { Snippet } from '@/components/common/Snippet';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useFormats } from '@/pages/bacen/shared';
import { crsfnSearchParams, fetchCrsfnSearch } from '@/services/bacen';

const PAGE_SIZE = 50;

/**
 * Full-text search over the RELATORIO, VOTO and dispositive of the linked
 * acordaos, accent-insensitive on both sides. A hit is an acordao whose text
 * matches; it says nothing about how the point was decided. With no terms
 * the reach panel describes the searchable corpus and nothing is listed; the
 * export is disabled then, because ten thousand rows of everything reads as
 * an answer.
 */
export function CrsfnSearchPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['bacen', 'crsfn', 'search', q],
    queryFn: () => fetchCrsfnSearch({ q, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('bacen:crsfnSearch.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('bacen:crsfnSearch.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="bacen:crsfnSearch.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/bacen/export/crsfn/search?${crsfnSearchParams({ q }).toString()}`}
          disabled={!q}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="crsfn-search" className="text-xs font-medium text-foreground">
            {t('bacen:crsfnSearch.search.label')}
          </label>
          <input
            id="crsfn-search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('bacen:crsfnSearch.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('bacen:crsfnSearch.search.submit')}
        </button>
      </form>

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <>
          <section className="rounded-lg border bg-card p-4" data-reach>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('bacen:crsfnSearch.reach.title')}
            </h2>
            <Answer answer={data.reach} help={t('bacen:crsfnSearch.reach.help')}>
              {(r) => (
                <p className="text-xs text-muted-foreground" data-reach-summary data-hits={r.hits}>
                  {r.terms
                    ? t('bacen:crsfnSearch.reach.withTerms', {
                        terms: r.terms,
                        hits: number.format(r.hits),
                        texts: number.format(r.linked_with_text),
                        linked: number.format(r.acordaos_linked),
                        share: percent.format(
                          r.acordaos_linked ? r.linked_with_text / r.acordaos_linked : 0,
                        ),
                      })
                    : t('bacen:crsfnSearch.reach.noTerms', {
                        texts: number.format(r.linked_with_text),
                        linked: number.format(r.acordaos_linked),
                        share: percent.format(
                          r.acordaos_linked ? r.linked_with_text / r.acordaos_linked : 0,
                        ),
                      })}
                </p>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="bacen:crsfnSearch.guide.reach" />
            </div>
          </section>

          {q ? (
            data.hits.length === 0 ? (
              <EmptyState hint={t('bacen:crsfnSearch.empty')} />
            ) : (
              <section
                className="rounded-lg border bg-card"
                data-hits
                data-stale={isPlaceholderData || undefined}
              >
                <div className="px-4 py-2 text-xs text-muted-foreground">
                  {t('bacen:crsfnSearch.count', {
                    listed: number.format(data.hits.length),
                    hits: number.format(data.reach.value?.hits ?? data.hits.length),
                  })}
                </div>
                <ol className="divide-y" aria-live="polite">
                  {data.hits.map((hit) => (
                    <li
                      key={hit.acordao_key}
                      className="space-y-1 px-4 py-3"
                      data-hit={hit.acordao_key}
                    >
                      <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                        <Link
                          to={`/bacen/crsfn/a/${hit.acordao_key}`}
                          className="font-medium text-primary underline-offset-2 hover:underline"
                        >
                          {t('bacen:case.acordaos.acordao', {
                            n: hit.numero_acordao,
                          })}
                        </Link>
                        <span className="text-xs text-muted-foreground">
                          {hit.judgment_date ?? hit.year ?? '—'}
                          {hit.relator ? ` · ${hit.relator}` : null}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <Snippet text={hit.headline} />
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            )
          ) : null}
        </>
      )}
    </div>
  );
}
